"use client";

import { BadgeCheck, CheckCircle2, Share2, ShieldCheck, Truck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ProductActions } from "@/components/product/product-actions";
import { ProductGallery } from "@/components/product/product-gallery";
import { Badge } from "@/components/ui/badge";
import { PrimeBadge } from "@/components/ui/prime-badge";
import { Rating } from "@/components/ui/rating";
import { cn, formatPrice } from "@/lib/utils";
import type { Product, ProductVariant } from "@/types";

export function ProductDetailView({ product }: { product: Product }) {
  const variants = product.variants && product.variants.length > 0 ? product.variants : [];
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    variants.length > 0 ? variants[0] : null
  );

  // Combine product.images and any unique variant sub-URLs
  const allImages = useMemo(() => {
    const set = new Set<string>();
    // First, variant image if selected
    if (selectedVariant?.image) set.add(selectedVariant.image);
    // Base images
    (product.images || []).forEach((img) => img && set.add(img));
    // Other variant images
    variants.forEach((v) => v.image && set.add(v.image));
    return Array.from(set);
  }, [product.images, variants, selectedVariant]);

  const [activeImage, setActiveImage] = useState<string>(
    selectedVariant?.image || product.images[0] || ""
  );

  function handleSelectVariant(variant: ProductVariant) {
    setSelectedVariant(variant);
    if (variant.image) {
      setActiveImage(variant.image);
    }
  }

  function handleShare() {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Product link copied to clipboard");
    }
  }

  const aboutPoints = useMemo(() => {
    const text = product.description?.trim() || "";
    if (!text) return [];

    const lines = text
      .split(/\r?\n+/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length > 1) {
      return lines.map((l) => l.replace(/^[•\-\*]\s*/, ""));
    }

    const sentences = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 20);

    if (sentences.length > 1) {
      return sentences.map((s) => s.replace(/^[•\-\*]\s*/, ""));
    }

    return [text];
  }, [product.description]);

  const effectivePrice = selectedVariant ? selectedVariant.price : product.price;
  const effectiveMrp = selectedVariant?.mrp ?? product.mrp;
  const effectiveDiscount =
    effectiveMrp > effectivePrice
      ? Math.round(((effectiveMrp - effectivePrice) / effectiveMrp) * 100)
      : product.discount;

  return (
    <section className="rounded border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900 sm:p-5">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,1.1fr)_260px] xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)_280px] lg:gap-8">
        {/* Left Column: Image Gallery with Sub-URLs */}
        <ProductGallery
          images={allImages}
          title={product.title}
          activeImage={activeImage}
          onSelectImage={setActiveImage}
        />

        {/* Middle Column: Details, Price, and Color Variants */}
        <div className="lg:border-l lg:border-slate-100 lg:pl-6 dark:lg:border-white/10">
          <h1 className="text-xl font-normal leading-snug text-slate-950 dark:text-white sm:text-2xl">
            {product.title}
          </h1>
          <p className="mt-1 text-sm text-amazon-teal">
            Brand:{" "}
            <Link
              href={`/search?q=${encodeURIComponent(product.brand)}`}
              className="hover:text-amazon-orange hover:underline"
            >
              {product.brand}
            </Link>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Rating value={product.rating} count={product.reviewCount} />
            {product.rating >= 4.3 && <Badge tone="success">MOVA&apos;s Choice</Badge>}
          </div>

          {/* Dynamic Price Box */}
          <div className="mt-3 border-y border-slate-200 py-3 dark:border-white/10">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-3xl font-medium text-slate-950 dark:text-white">
                {formatPrice(effectivePrice)}
              </span>
              {effectiveDiscount > 0 && (
                <span className="text-sm font-semibold text-amazon-red">
                  -{effectiveDiscount}%
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              M.R.P.: <span className="line-through">{formatPrice(effectiveMrp)}</span>
            </p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              EMI from {formatPrice(Math.ceil(effectivePrice / 12))}/mo
            </p>
            {product.isPrime && <PrimeBadge className="mt-2" />}
          </div>

          {/* Color Variants with Individual Sub-URLs & Prices */}
          {variants.length > 0 && (
            <div className="mt-4 border-b border-slate-200 pb-4 dark:border-white/10">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Color:{" "}
                <span className="font-semibold text-amazon-orange">
                  {selectedVariant?.color ?? "Select a color"}
                </span>
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2.5">
                {variants.map((v) => {
                  const isSelected = selectedVariant?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => handleSelectVariant(v)}
                      className={cn(
                        "group relative flex items-center gap-2 rounded-lg border-2 p-1.5 pr-3 text-left transition focus:outline-none",
                        isSelected
                          ? "border-amazon-orange bg-amber-50/60 shadow-sm ring-2 ring-amazon-orange/40 dark:bg-amber-950/20"
                          : "border-slate-200 hover:border-slate-400 dark:border-white/10 dark:hover:border-white/30"
                      )}
                    >
                      {/* Variant Sub-URL Thumbnail */}
                      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded border border-slate-200 bg-white dark:border-white/10">
                        {v.image ? (
                          <Image
                            src={v.image}
                            alt={v.color}
                            fill
                            sizes="40px"
                            className="object-contain p-0.5"
                          />
                        ) : (
                          <div
                            className="h-full w-full"
                            style={{ backgroundColor: v.colorCode || "#000" }}
                          />
                        )}
                      </div>

                      {/* Color Details & Variant-specific Price */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-3 w-3 shrink-0 rounded-full border border-black/20"
                            style={{ backgroundColor: v.colorCode || "#333" }}
                          />
                          <p className="truncate text-xs font-bold text-slate-900 dark:text-white">
                            {v.color}
                          </p>
                        </div>
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                          {formatPrice(v.price)}
                        </p>
                      </div>

                      {isSelected && (
                        <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amazon-orange text-[10px] font-black text-white shadow">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Project & Delivery Assurance */}
          <div className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <p className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 shrink-0 text-amazon-teal" />
              <span>100% Genuine product guaranteed by MOVA Market</span>
            </p>
            <p className="flex items-center gap-2">
              <Truck className="h-4 w-4 shrink-0 text-amazon-teal" />
              <span>Fast & reliable delivery available across Ethiopia</span>
            </p>
            <p className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 text-amazon-teal" />
              <span>Cash on Delivery (COD) & secure payment supported</span>
            </p>
            {product.warranty && (
              <p className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-amazon-teal" />
                <span>Warranty: {product.warranty}</span>
              </p>
            )}
          </div>

          {/* About this item (Quick Highlights) */}
          {aboutPoints.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-white/10">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                About this item
              </h3>
              <ul className="mt-2.5 space-y-2 pl-4 text-xs leading-relaxed text-slate-700 list-disc marker:text-slate-400 dark:text-slate-300 sm:text-sm">
                {aboutPoints.slice(0, 5).map((bullet, idx) => (
                  <li key={idx} className="leading-snug">
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Mobile Actions */}
          <div className="mt-4 lg:hidden">
            <ProductActions product={product} selectedVariant={selectedVariant} compact />
          </div>

          <button
            type="button"
            onClick={handleShare}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-amazon-teal hover:text-amazon-orange"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share
          </button>
        </div>

        {/* Right Column (Desktop Purchase Box) */}
        <div className="hidden space-y-3 lg:block">
          <ProductActions product={product} selectedVariant={selectedVariant} />
        </div>
      </div>
    </section>
  );
}
